import React, { useState, useEffect } from "react";
import "../../styles/atoms/link-picker.css";

export interface LinkOption {
  id: string;
  label: string;
}

interface LinkPickerProps {
  label: string;
  placeholder: string;
  options: LinkOption[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
}

const LinkPicker: React.FC<LinkPickerProps> = ({
  label,
  placeholder,
  options,
  value = null,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(value);
  const refId = useState(() => Math.random().toString(36).slice(2))[0];

  useEffect(() => {
    setSelected(value);
  }, [value]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const hasClosest = !!(target && typeof (target as any).closest === 'function');
      const inside = hasClosest && (target as any).closest(`[data-linkpicker="${refId}"]`);
      if (!inside) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [refId]);

  const handleSelect = (optionId: string) => {
    setSelected(optionId);
    setOpen(false);
    onChange?.(optionId);
  };

  const handleRemove = () => {
    setSelected(null);
    onChange?.(null);
  };
  
  const selectedOption = options.find(opt => opt.id === selected);
  const displayLabel = selectedOption ? selectedOption.label : null;

  return (
    <div className="link-picker" data-linkpicker={refId}>
      <div className="link-picker__label">{label}</div>

      {!selected ? (
        <button
          type="button"
          className="link-picker__trigger"
          onClick={() => !disabled && setOpen((v) => !v)}
          disabled={disabled}
        >
          {`+ ${placeholder}`}
        </button>
      ) : (
        <div className="link-picker__pill">
          <div className="link-picker__pill-text">{displayLabel || selected}</div>
          <button
            type="button"
            aria-label="Remove link"
            className="link-picker__trash"
            onClick={handleRemove}
            disabled={disabled}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6H5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M9 6V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M18 6l-1 13a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 6" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M10 11v6" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
              <path d="M14 11v6" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      )}

      {open && !disabled && (
        <div className="link-picker__menu">
          {options.length === 0 ? (
            <div className="link-picker__option" style={{ color: '#9CA3AF', cursor: 'default' }}>
              No options available
            </div>
          ) : (
            options.map((opt) => (
              <div
                key={opt.id}
                className={`link-picker__option ${selected === opt.id ? 'link-picker__option--selected' : ''}`}
                onClick={() => handleSelect(opt.id)}
              >
                {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LinkPicker;

